"use client";

/**
 * An event's page: the pieces of src/components/event arranged for a phone --
 * one column under the hero, the way to register in a bar along the bottom --
 * and for a desktop, two columns 3:2 on a white panel, as the current app lays
 * them out (the event-views skill). The server page hands over the event as
 * everyone sees it; what is the visitor's own -- registered, still to pay, the
 * places left -- is asked for here on mount, with the programme and what to bring.
 *
 * Which layout is drawn is read from the viewport through useSyncExternalStore:
 * on the server, and while hydrating, both are drawn and CSS shows one, so the
 * first paint is right whatever the device; from then on only the one.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { motion } from "motion/react";
import { ChevronLeft, CircleCheck, Link2, Mail, MapPinned, Map as MapIcon, Share2 } from "lucide-react";
import { HikingIcon } from "@/components/ui/HikingIcon";
import { api } from "@/lib/api";
import { eventPath, TAB_PATHS } from "@/lib/address";
import { showingEvent, track } from "@/lib/analytics";
import { isPhoneNow } from "@/lib/device";
import { euros } from "@/lib/format";
import { useNavigation } from "@/lib/navigation";
import { useSession } from "@/lib/session";
import type { EquipmentItem, EventDetails, EventSchedule as Schedule, EventWithState, Photo, ScheduleEntry } from "@/lib/types/event";
import { PageHeader, StackedPage } from "@/components/shell/PageHeader";
import { PageView } from "@/components/shell/PageView";
import { SiteFooter } from "@/components/shell/SiteFooter";
import { Section } from "@/components/ui/Section";
import { Sheet } from "@/components/ui/Sheet";
import { WhatsAppIcon } from "@/components/ui/BrandIcons";
import { RegisterButton } from "@/components/explore/RegisterButton";
import { EventBanner } from "@/components/event/EventBanner";
import { EventTestimonials } from "@/components/event/EventTestimonials";
import type { Testimonials } from "@/lib/types/testimonial";
import { EventFacts } from "@/components/event/EventFacts";
import { EventDescription } from "@/components/event/EventDescription";
import { EventLocation, EventLocationLinks, EventLocationMap, hasLocation } from "@/components/event/EventLocation";
import { EventSchedule } from "@/components/event/schedule";
import { EventEquipment } from "@/components/event/EventEquipment";
import { EventPhotos } from "@/components/event/EventPhotos";
import { UpcomingStrip } from "@/components/home/UpcomingStrip";
import type { HomeEvent } from "@/components/home/model";
import { galleryMedia } from "@/components/event/media";
import { PriceCoverage, hasPriceCoverage } from "@/components/event/PriceCoverage";
import { TrailsSummary } from "@/components/event/TrailsSummary";
import { CompanyInfo } from "@/components/event/CompanyInfo";
import { TrailPopup, type TrailPopupTarget } from "@/components/event/TrailPopup";
import { LeaderHost } from "@/components/event/LeaderPopup";
import { FaqHost } from "@/components/event/DifficultyBar";
import { RegistrationOpener } from "@/components/event/RegistrationOpener";
import { WaitingListOpener } from "@/components/waiting/WaitingListOpener";
import { SHARE_TARGETS, shareNatively, shareThrough, type ShareTarget } from "@/components/event/share";
import { asPreviewed } from "@/lib/preview";
import { realLisbonNow } from "@/lib/eventTime";
import { eventEnded, isGoing, isSoldOutFor, trailTitle } from "@/components/event/labels";

/** the event a registration's Ver programa asks the page to show: window.dispatchEvent(new CustomEvent(SHOW_PROGRAMME_EVENT, {detail: {eventId}})) */
export const SHOW_PROGRAMME_EVENT = "montanhas:show-programme";

type Layout = "phone" | "desktop" | "both";

const PHONE_QUERY = "(max-width: 39.99rem)";
const subscribe = (listener: () => void) => {
  const media = window.matchMedia(PHONE_QUERY);
  media.addEventListener("change", listener);
  return () => media.removeEventListener("change", listener);
};
const useLayout = (): Layout =>
  useSyncExternalStore(
    subscribe,
    () => (isPhoneNow() ? "phone" : "desktop"),
    () => "both",
  );

const CLIENT_FIELDS = ["is_registered", "awaiting_payment", "amount_due", "payment_link", "is_favorite", "on_waiting_list", "availability", "places_left", "registration_state", "state"] as const;

const NO_OTHERS: HomeEvent[] = [];
// what GA is told the list of other events is called
const OTHERS_LIST = "Evento · Outros eventos";

// a programme with nothing in it is no programme
const scheduleOf = (answer: Schedule | null | undefined) => (answer && answer.entries && answer.entries.length ? answer : null);

/**
 * `schedule` and `equipment` are the programme and the list of what to bring as
 * the server already asked for them, so that both are in the page's HTML, where
 * a search engine reads them; left out (the page drawn in the browser alone),
 * the page asks for them itself. Null is an event with none.
 */
export function EventPage({
  event: given,
  schedule: givenSchedule,
  equipment: givenEquipment,
  testimonials: givenTestimonials,
  others = NO_OTHERS,
}: {
  event: EventDetails;
  schedule?: Schedule | null;
  equipment?: EquipmentItem[] | null;
  /** what clients said of the programme's past events (montanhas-web#19), as the server asked for it; left out, the page asks itself */
  testimonials?: Testimonials | null;
  /** the next events but this one, which the page ends with */
  others?: HomeEvent[];
}) {
  const layout = useLayout();
  const { client } = useSession();
  const [state, setState] = useState<Partial<EventWithState>>({});
  const [stateReady, setStateReady] = useState(false);
  const [schedule, setSchedule] = useState<Schedule | null | undefined>(givenSchedule === undefined ? undefined : scheduleOf(givenSchedule));
  const [equipment, setEquipment] = useState<EquipmentItem[] | null | undefined>(givenEquipment);
  const [testimonials, setTestimonials] = useState<Testimonials | null | undefined>(givenTestimonials);
  const fromServer = givenSchedule !== undefined && givenEquipment !== undefined;
  const [trail, setTrail] = useState<TrailPopupTarget | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  // in a preview from the back-office, with the states it would have at the moment previewed
  const event = useMemo<EventWithState>(() => asPreviewed({ ...given, ...state }, realLisbonNow()), [given, state]);
  const id = event.id;

  // the page's address given the title as it reads today, keeping history.state
  useEffect(() => {
    const path = eventPath(event);
    if (window.location.pathname.replace(/\/+$/, "") !== path) {
      window.history.replaceState(window.history.state, "", `${path}${window.location.search}${window.location.hash}`);
    }
    showingEvent(event);
    // the title alone decides the address; the rest of the event does not
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, given.title]);

  // what is the visitor's own, asked for again whenever who is signed in changes
  const clientId = client?.id ?? null;
  useEffect(() => {
    let cancelled = false;
    api.get<EventWithState[]>(`/public-api/events?event_id=${id}&past=6_MONTHS`, { silent: true }).then((list) => {
      if (cancelled) return;
      const live = list && list[0];
      if (live) {
        const next: Partial<EventWithState> = {};
        CLIENT_FIELDS.forEach((field) => {
          if (field in live) {
            (next as Record<string, unknown>)[field] = live[field];
          }
        });
        setState(next);
      }
      setStateReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [id, clientId]);

  useEffect(() => {
    if (fromServer) return;
    let cancelled = false;
    api.get<Schedule>(`/public-api/event/${id}/schedule`, { silent: true }).then((answer) => {
      if (!cancelled) setSchedule(scheduleOf(answer));
    });
    api.get<EquipmentItem[]>(`/public-api/event/${id}/equipment`, { silent: true }).then((answer) => {
      if (!cancelled) setEquipment(Array.isArray(answer) ? answer : null);
    });
    return () => {
      cancelled = true;
    };
  }, [id, fromServer]);

  useEffect(() => {
    if (givenTestimonials !== undefined) return;
    let cancelled = false;
    api.get<Testimonials>(`/public-api/event/${id}/testimonials`, { silent: true }).then((answer) => {
      if (!cancelled) setTestimonials(answer);
    });
    return () => {
      cancelled = true;
    };
  }, [id, givenTestimonials]);

  // the programme opened at a trail, from the trails at a glance or a registration's Ver programa.
  // The programme is drawn again with that trail's line already open, so the page
  // scrolls to it at the height it will have
  const [programme, setProgramme] = useState<{ key: number; entry: number | null }>({ key: 0, entry: null });
  const revealProgramme = useCallback((entryId: number | null = null) => {
    setProgramme((p) => ({ key: p.key + 1, entry: entryId }));
  }, []);
  useEffect(() => {
    if (!programme.key) return;
    const target = programme.entry;
    let observer: IntersectionObserver | null = null;
    let fallback: ReturnType<typeof setTimeout> | undefined;
    const frame = requestAnimationFrame(() => {
      const entry = target !== null ? document.getElementById(`entry-${target}`) : null;
      (entry || document.getElementById("programa"))?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (!entry) return;
      // the trail asked for lights up twice, so it is found among the others: once
      // the page has brought it into the upper half of the screen, or after a
      // moment for one too near the end of the page to get that far
      const flash = () => {
        observer?.disconnect();
        clearTimeout(fallback);
        // --brand-soft a shade deeper, to show on a white page
        const tint = "hsl(142 52% 88%)";
        const lit = { backgroundColor: tint, boxShadow: `0 0 0 8px ${tint}`, borderRadius: "6px" };
        const unlit = { backgroundColor: "transparent", boxShadow: "0 0 0 8px transparent", borderRadius: "6px" };
        entry.animate([unlit, lit, unlit, lit, unlit], { duration: 1800, delay: 150, easing: "ease-in-out" });
      };
      observer = new IntersectionObserver(([seen]) => seen.isIntersecting && flash(), { rootMargin: "0px 0px -50% 0px" });
      observer.observe(entry);
      fallback = setTimeout(flash, 1500);
    });
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      clearTimeout(fallback);
    };
  }, [programme]);
  useEffect(() => {
    const onShow = (e: Event) => {
      const detail = (e as CustomEvent<{ eventId?: unknown }>).detail;
      if (schedule && String(detail?.eventId ?? id) === String(id)) {
        revealProgramme();
      }
    };
    window.addEventListener(SHOW_PROGRAMME_EVENT, onShow);
    return () => window.removeEventListener(SHOW_PROGRAMME_EVENT, onShow);
  }, [id, schedule, revealProgramme]);

  const totalKm = useMemo(() => {
    const total = (schedule?.entries || []).reduce((sum, entry) => sum + ((entry.trail && Number(entry.trail.length)) || 0), 0);
    return total > 0 ? total / 1000 : null;
  }, [schedule]);

  const ended = eventEnded(event);
  const cancelled = event.state === "CANCELED";
  const registerShown = !ended && !cancelled && (event.registration_state === "OPEN" || !!event.is_registered);
  const going = isGoing(event);

  const openTrail = useCallback(
    (entry: ScheduleEntry) => {
      if (!entry.trail) return;
      track("view_trail", { item_id: String(id), trail_id: String(entry.trail.id), trail_name: entry.trail.title });
      setTrail({ eventId: id, trailId: entry.trail.id, variantId: entry.trail.variant?.id ?? 0, title: trailTitle(entry.trail) });
    },
    [id],
  );
  // the gallery: the photos with the videos among them
  const media = useMemo(() => galleryMedia(event.photos, event.videos), [event.photos, event.videos]);
  const onSchedulePhotos = useCallback(() => track("view_photos", { item_id: String(id), where: "programa" }), [id]);
  const onTrailBox = useCallback(
    (entry: ScheduleEntry) => {
      track("view_trails_summary", { item_id: String(id) });
      revealProgramme(entry.id);
    },
    [id, revealProgramme],
  );
  const share = useCallback(() => {
    if (!shareNatively(event)) {
      setShareOpen(true);
    }
  }, [event]);

  const pieces = {
    schedule: schedule ? (
      <EventSchedule
        key={programme.key}
        schedule={schedule}
        open
        openEntry={programme.entry}
        onOpenTrail={going ? (_, entry) => openTrail(entry) : undefined}
        onOpenPhotos={onSchedulePhotos}
      />
    ) : null,
    equipment: equipment === null ? null : <EventEquipment items={equipment} />,
  };

  return (
    <>
        <PageView id={`evento/${id}`} title={event.title} />
        <RegistrationOpener event={event} ready={stateReady} />
        <WaitingListOpener event={event} ready={stateReady} />
        {(layout === "phone" || layout === "both") && (
          <PhoneLayout
            event={event}
            schedule={schedule}
            equipment={equipment}
            testimonials={testimonials}
            media={media}
            totalKm={totalKm}
            registerShown={registerShown}
            programmeKey={programme.key}
            pieces={pieces}
            others={others}
            onTrailBox={onTrailBox}
            onShare={share}
            showMap={layout === "phone"}
            className={layout === "both" ? "desk:hidden" : ""}
          />
        )}
        {(layout === "desktop" || layout === "both") && (
          <DesktopLayout
            event={event}
            schedule={schedule}
            equipment={equipment}
            testimonials={testimonials}
            media={media}
            totalKm={totalKm}
            registerShown={registerShown}
            programmeKey={programme.key}
            pieces={pieces}
            others={others}
            onTrailBox={onTrailBox}
            onShare={share}
            showMap={layout === "desktop"}
            className={layout === "both" ? "hidden desk:block" : ""}
          />
        )}
        <TrailPopup target={trail} onClose={() => setTrail(null)} />
        <LeaderHost />
        <FaqHost />
        <ShareSheet event={event} open={shareOpen} onClose={() => setShareOpen(false)} />
    </>
  );
}

type LayoutProps = {
  event: EventWithState;
  schedule: Schedule | null | undefined;
  equipment: EquipmentItem[] | null | undefined;
  /** the gallery: the photos with the videos among them */
  media: Photo[];
  totalKm: number | null;
  registerShown: boolean;
  programmeKey: number;
  pieces: { schedule: ReactNode; equipment: ReactNode };
  others: HomeEvent[];
  testimonials: Testimonials | null | undefined;
  onTrailBox: (entry: ScheduleEntry) => void;
  onShare: () => void;
  showMap: boolean;
  className?: string;
};

const Heading = ({ icon: Icon, children }: { icon: typeof MapIcon; children: ReactNode }) => (
  <span className="inline-flex items-center gap-2">
    <Icon size={18} className="text-brand" /> {children}
  </span>
);

const FADE = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.26, ease: [0.2, 0.8, 0.2, 1] as const } };

function PhoneLayout({ event, schedule, equipment, media, totalKm, registerShown, pieces, others, testimonials, onTrailBox, onShare, showMap, className = "" }: LayoutProps) {
  return (
    <div className={`min-h-dvh bg-surface ${className}`}>
      <StackedPage back={TAB_PATHS.explore}>
        <PageHeader
          title={event.title}
          back={TAB_PATHS.explore}
          actions={
            <button type="button" onClick={onShare} className="pressable flex h-10 w-10 items-center justify-center rounded-full text-ink" aria-label="Partilhar">
              <Share2 size={21} />
            </button>
          }
        />
        <EventBanner event={event} className="aspect-[16/9] w-full rounded-b-[22px]" />
        <motion.div {...FADE} className="px-4 pt-4" style={{ paddingBottom: `calc(var(--tabbar-h) + var(--safe-bottom) + ${registerShown ? (event.is_registered ? 164 : 88) : 24}px)` }}>
          {/* the walking at a glance comes first on a phone, straight under the banner */}
          <TrailsSummary schedule={schedule} onOpen={onTrailBox} className="mb-4" />
          <h1 className="text-[23px] font-extrabold leading-tight tracking-tight">{event.title}</h1>
          <EventFacts event={event} totalKm={totalKm} rating={testimonials} className="mt-3" />
          <EventDescription text={event.short_description} className="mt-4" />
          <EventPhotos photos={media} eventId={event.id} title={event.title} className="mt-4" />
          <div className="mt-4">
            {hasLocation(event) && (
              // the map starts folded away on a phone; the way there stays in sight under the heading
              <Section title={<Heading icon={MapPinned}>Localização</Heading>} id="localizacao" open={false} footer={<EventLocationLinks event={event} />}>
                <EventLocationMap event={event} height={200} showMap={showMap} />
              </Section>
            )}
            {schedule && (
              <Section title={<Heading icon={MapIcon}>Programa</Heading>} id="programa">
                {pieces.schedule}
              </Section>
            )}
            {hasPriceCoverage(event) && (
              <Section title={<Heading icon={CircleCheck}>O que está incluído</Heading>} id="incluido">
                <PriceCoverage event={event} />
              </Section>
            )}
            {equipment !== null && (
              <Section title={<Heading icon={HikingIcon}>O que levar</Heading>} id="levar" open={false} keep>
                {pieces.equipment}
              </Section>
            )}
          </div>
          <EventTestimonials of={testimonials} className="mt-6 border-t border-line pt-5" />
          <UpcomingStrip events={others} title="Outros eventos" list={OTHERS_LIST} className="mt-6 border-t border-line pt-5" />
          <CompanyInfo className="mt-6 border-t border-line pt-4" />
        </motion.div>
      </StackedPage>
      {registerShown && <PhoneRegisterBar event={event} />}
    </div>
  );
}

/** the way to register, in a bar along the bottom of a phone's screen, above the tabs, so it is always in reach */
function PhoneRegisterBar({ event }: { event: EventWithState }) {
  const price = Number(event.price) || 0;
  const wide = !!event.is_registered || isSoldOutFor(event);
  return (
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 420, damping: 36, delay: 0.1 }}
      className="hairline-t fixed inset-x-0 z-30 bg-surface/95 px-4 py-2.5 backdrop-blur-md desk:hidden"
      style={{ bottom: "calc(var(--tabbar-h) + var(--safe-bottom))" }}
    >
      <div className="flex items-center gap-3">
        {/* a full event gives the bar to its waiting list: the price is of no use there */}
        {!wide && (
          <div className="min-w-0 flex-1">
            <div className="text-[18px] font-extrabold leading-tight text-price">{event.price == null ? "" : price > 0 ? euros(price) : "Gratuito"}</div>
            <div className="truncate text-[11.5px] text-ink-soft">
              {Number(event.price_child) > 0 ? `por adulto · criança ${euros(event.price_child)}` : price > 0 ? "por pessoa" : ""}
            </div>
          </div>
        )}
        <div className={wide ? "w-full" : "shrink-0"}>
          <RegisterButton event={event} size="md" full={wide} />
        </div>
      </div>
    </motion.div>
  );
}

function DesktopLayout({ event, schedule, equipment, media, totalKm, registerShown, pieces, others, testimonials, onTrailBox, onShare, showMap, className = "" }: LayoutProps) {
  const { goBack } = useNavigation();
  const registerBlock = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(false);

  // the way to register comes back pinned under the top bar once the page has been scrolled past it
  useEffect(() => {
    const node = registerBlock.current;
    if (!(node instanceof Element) || !registerShown) return;
    const observer = new IntersectionObserver(([entry]) => setPinned(!entry.isIntersecting && entry.boundingClientRect.top < 0), { rootMargin: "-64px 0px 0px 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [registerShown]);

  return (
    <>
    <div className={`mx-auto w-full max-w-[1200px] px-6 pt-5 ${className}`}>
      <div className="flex h-10 items-center gap-2">
        <button type="button" onClick={() => goBack(TAB_PATHS.explore)} className="pressable flex h-10 w-10 items-center justify-center rounded-full bg-surface text-ink shadow-card hover:bg-ground" aria-label="Voltar">
          <ChevronLeft size={24} strokeWidth={2.4} />
        </button>
        <span className="flex-1" />
        <button type="button" onClick={onShare} className="pressable inline-flex h-10 items-center gap-2 rounded-full bg-surface px-4 text-[14px] font-bold text-ink shadow-card hover:bg-ground">
          <Share2 size={17} /> Partilhar
        </button>
      </div>
      {registerShown && (
        <motion.div
          initial={false}
          animate={{ y: pinned ? 0 : -80, opacity: pinned ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          className={`fixed inset-x-0 top-16 z-30 ${pinned ? "" : "pointer-events-none"}`}
          aria-hidden={!pinned}
        >
          <div className="mx-auto max-w-[1200px] px-6">
            <div className="flex items-center gap-4 rounded-b-[14px] border border-line bg-surface/95 px-5 py-2.5 shadow-card backdrop-blur-md">
              <span className="min-w-0 flex-1 truncate text-[15px] font-bold">{event.title}</span>
              <RegisterButton event={event} size="md" full={false} panel={false} />
            </div>
          </div>
        </motion.div>
      )}
      <motion.div {...FADE} className="card mt-4 p-6">
        <div className="grid grid-cols-[3fr_2fr] gap-8">
          <div className="min-w-0">
            <div className={`grid gap-2 ${media.length ? "grid-cols-[3fr_1fr]" : "grid-cols-1"}`}>
              <EventBanner event={event} className="aspect-[16/9] w-full rounded-[14px]" transition={showMap} />
              {media.length > 0 && <EventPhotos photos={media} eventId={event.id} title={event.title} variant="column" />}
            </div>
            <EventDescription text={event.short_description} className="mt-5" />
            <div className="mt-3">
              {schedule && (
                <Section title={<Heading icon={MapIcon}>Programa</Heading>} id="programa">
                  {pieces.schedule}
                </Section>
              )}
              {equipment !== null && (
                <Section title={<Heading icon={HikingIcon}>O que levar</Heading>} id="levar" open={false} keep>
                  {pieces.equipment}
                </Section>
              )}
            </div>
          </div>
          <div className="min-w-0">
            <h1 className="text-[26px] font-extrabold leading-tight tracking-tight">{event.title}</h1>
            <TrailsSummary schedule={schedule} onOpen={onTrailBox} className="mt-4" />
            <EventFacts event={event} totalKm={totalKm} rating={testimonials} className="mt-4" />
            {registerShown && (
              <div ref={registerBlock} className="mt-5">
                <RegisterButton event={event} size="lg" full />
              </div>
            )}
            <div className="mt-3">
              {hasLocation(event) && (
                <Section title={<Heading icon={MapPinned}>Localização</Heading>} id="localizacao">
                  <EventLocation event={event} height={300} showMap={showMap} />
                </Section>
              )}
              {hasPriceCoverage(event) && (
                <Section title={<Heading icon={CircleCheck}>O que está incluído</Heading>} id="incluido">
                  <PriceCoverage event={event} />
                </Section>
              )}
            </div>
          </div>
        </div>
        <EventTestimonials of={testimonials} className="mt-8 border-t border-line pt-6" />
        <UpcomingStrip events={others} title="Outros eventos" list={OTHERS_LIST} className="mt-8 border-t border-line pt-6" />
        <CompanyInfo className="mt-8 border-t border-line pt-4" />
      </motion.div>
    </div>
    <SiteFooter />
    </>
  );
}

const SHARE_ICONS: Record<ShareTarget["id"], ReactNode> = {
  whatsapp: <WhatsAppIcon size={20} />,
  facebook: (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13.5 22v-8h2.7l.4-3.2h-3.1V8.8c0-.9.3-1.6 1.6-1.6h1.7V4.4c-.3 0-1.3-.1-2.5-.1-2.5 0-4.1 1.5-4.1 4.3v2.2H7.4V14h2.8v8h3.3z" />
    </svg>
  ),
  x: <span className="text-[16px] font-extrabold">X</span>,
  email: <Mail size={20} />,
  copy: <Link2 size={20} />,
};

/** the ways to share, for a device with no share sheet of its own */
function ShareSheet({ event, open, onClose }: { event: EventWithState; open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="Partilhar" width={360}>
      <ul className="px-2 pb-4">
        {SHARE_TARGETS.map((target) => (
          <li key={target.id}>
            <button
              type="button"
              className="pressable flex w-full items-center gap-3 rounded-[12px] px-3 py-3 text-left text-[15px] font-semibold hover:bg-ground"
              onClick={() => {
                onClose();
                shareThrough(event, target);
              }}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-soft text-brand">{SHARE_ICONS[target.id]}</span>
              {target.label}
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}
